<template>
  <div class="min-h-page">
    <div class="flex justify-between items-center py-4 px-3 md:px-6">
      <div class="flex-auto relative">
        <h2 v-text="sectionTitle" @click="maybeSearch" class="text-xl md:text-2xl lg:text-3xl font-bold text-red-600 print:text-black truncate max-w-section-title"></h2>
        <div :class="{ searchActive }" class="search-wrap md:block">
          <div class="rounded-full flex">
            <div class="flex-auto overflow-hidden">
              <div v-if="searchActive" class="rounded top-0 right-0 relative">
                <!-- <a @mousedown="close" class="absolute left-0 top-0 ml-1 p-2 cursor-pointer">
                  <svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg"><line x1="1.35355" y1="0.646447" x2="17.3536" y2="16.6464" stroke="black"/><line y1="-0.5" x2="22.6274" y2="-0.5" transform="matrix(-0.707107 0.707107 0.707107 0.707107 17 1)" stroke="black"/></svg>
                </a> -->
                <input v-focus v-model="form.search" @input="search" @blur="hide" @keyup.esc="searchActive = false" type="text" placeholder="Suche" class="outline-none w-full bg-transparent px-5 py-2 text-red-600 text-xl" autocomplete="off">
              </div>
            </div>
            <button @click="magnify" :class="{ 'hidden': hasSearch && !searchActive }" class="search-btn focus:outline-none flex-none sm:block">
              <svg width="27px" height="27px" viewBox="0 0 39 39" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                  <g transform="translate(0.000000, -1.000000)" stroke="#fff" stroke-width="4">
                    <path d="M22.5,31 C30.2319865,31 36.5,24.7319865 36.5,17 C36.5,9.2680135 30.2319865,3 22.5,3 C14.7680135,3 8.5,9.2680135 8.5,17 C8.5,24.7319865 14.7680135,31 22.5,31 Z M11.5,28 L1.5,38"></path>
                  </g>
                </g>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div v-show="showZuruck" class="print:hidden">
        <button @click="zuruck" class="btn w-zuruck h-zuruck focus:outline-none">
          <span class="md:hidden"><close width="30"/></span>
          <span class="hidden md:inline">{{ searchActive ? 'abbrechen' : 'zurück' }}</span>
        </button>
      </div>
    </div>
    <div v-if="!searchResults">
      <div class="hidden md:flex items-center py-4 px-6 border-t font-bold">
        <div class="w-48 flex-none">Sammlung</div>
        <div class="flex-auto">Titel <span class="hidden">⇅</span></div>
        <div class="w-48 flex-none">Datum</div>
        <div class="w-16 flex-none text-center"> </div>
      </div>
      <template v-for="post in posts">
        <post-row :post="post" :key="post.id"/>
      </template>
      <div v-if="totalPages > 1" class="pt-16 pb-4 px-6 border-t text-sm md:text-base">
        <div class="flex flex-wrap items-center justify-center">
          <div class="flex-none">
            <router-link :to="prevPage" :class="{ 'opacity-25': prevPage === $route.path }" class="block px-6 py-2 rounded-full bg-gray-300 hover:bg-red-500 transition-colors duration-200">Vorherige Seite</router-link>
          </div>
          <div class="flex-auto w-full md:w-auto mt-6 mb-2 md:my-0">
            <ul class="flex flex-wrap justify-center">
              <li v-for="(p, i) in pageNumbers" :key="i" :class="activeLink(p)" class="rounded-full mx-1 transition-colors duration-200 pagenumbers mb-4 md:mb-0">
                <router-link v-if="p !== '...'" :to="pageLink(p)" class="block px-4 py-2">{{ p }}</router-link>
                <span v-else class="block px-4 py-2">{{ p }}</span>
              </li>
            </ul>
          </div>
          <div class="flex-none">
            <router-link :to="nextPage" :class="{ 'opacity-25': nextPage === $route.path }" class="block px-6 py-2 rounded-full bg-gray-300 hover:bg-red-500 transition-colors duration-200">Nächste Seite</router-link>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <template v-for="post in searchResults">
        <search-row :post="post" :key="post.id"/>
      </template>
      <div v-if="searching">
        <div class="flex items-start p-4 lg:px-6 border-t">
          <div class="text-gray-500">Suchen...</div>
        </div>
      </div>
      <div v-if="!searchResults.length">
        <div class="flex items-start p-4 lg:px-6 border-t">
          <div>Keine Ergebnisse</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import store from '@/store'
  import { mapState, mapGetters } from 'vuex'
  import PostRow from '@/components/PostRow'
  import SearchRow from '@/components/SearchRow'
  import Close from '@/components/icons/Close'
  import axios from 'axios'
  import debounce from 'debounce'

  const searchEndpoint = process.env.VUE_APP_API_URL + '/gkb/v1/search'

  let call = null
  const once = (config = {}) => {
    config.url = searchEndpoint
    if (call) {
      call.cancel('Only one request allowed at a time.')
    }
    call = axios.CancelToken.source()

    config.cancelToken = call.token
    return axios(config)
  }

  export default {
    name: 'Home',
    metaInfo () {
      return {
        title: this.pageTitle
      }
    },
    data () {
      return {
        searchActive: false,
        form: { search: null },
        searchResults: null,
        searching: false,
        selected: 0,
        lastNonSearchRoute: null,

        filters: {
          page: 1,
          category: null,
          tag: null
        }
      }
    },
    computed: {
      page () {
        const p = this.$route.params.page || 1
        return parseInt(p)
      },
      pageTitle () {
        if (this.hasSearch) {
          return `${this.hasSearch} - suchergebnisse`
        }
        const { category, tag } = this.$route.params
        const key = category || tag
        if (key) {
          const name = this.taxNames[key]
          return name ? name.toLowerCase() : key
        }
        return ''
      },
      pageBase () {
        const { category, tag } = this.$route.params
        let base = '/'
        if (category) {
          base = `/sammlung/${category}`
        } else if (tag) {
          base = `/tag/${tag}`
        }
        return base
      },
      prevPage () {
        if (this.page < 3) return this.pageBase
        const path = `${this.pageBase}/page/${this.page - 1}`
        return path.replace('//', '/')
      },
      nextPage () {
        if (this.page >= this.totalPages) return this.$route.path
        const path = `${this.pageBase}/page/${this.page + 1}`
        return path.replace('//', '/')
      },
      hasQuery () {
        return this.form.search && this.form.search.length >= 2
      },
      noResults () {
        return this.hasQuery && (this.searchResults === false || !this.searchResults.length)
      },
      results () {
        if (!this.searchResults) return []
        return this.searchResults
      },
      count () {
        return this.results.length
      },
      hasSearch () {
        return this.$route.query?.s
      },
      filterTitle () {
        const opts = {
          'posts-category': 'Sammlung',
          'posts-tag': 'Tag'
        }
        let str = opts[this.$route.name]
        if (!str) return

        const { category, tag } = this.$route.params
        const key = category || tag
        return `${str}: ${this.taxNames[key]}`
      },
      sectionTitle () {
        if (this.hasSearch) return `Suche: ${this.hasSearch}`
        return this.filterTitle || 'Journal'
      },
      showZuruck () {
        const to = this.lastNonSearchRoute || '/'
        return to !== this.$route.fullPath
      },

      pageNumbers () {
        const l = [1]
        const p = []
        const b = this.page - 1
        const f = this.page + 1
        if (b > 0 && !l.includes(b)) {
          p.push(b)
        }
        if (!l.includes(this.page)) {
          p.push(this.page)
        }
        if (f < this.totalPages && !l.includes(f)) {
          p.push(f)
        }

        if (p[0] - l[0] > 1) {
          l.push('...')
        }

        if (this.totalPages - p[p.length - 1] > 1) {
          p.push('...')
        }

        l.push(...p)
        !l.includes(this.totalPages) && l.push(this.totalPages)

        return l
      },

      ...mapState(['posts', 'totalPosts', 'totalPages']),
      ...mapGetters(['taxNames'])
    },
    methods: {
      getPosts (page = 1) {
        return this.$store.dispatch('getPosts', page)
      },
      pageLink (n) {
        if (n === 1) return this.pageBase
        const path = `${this.pageBase}/page/${n}`
        return path.replace('//', '/')
      },
      activeLink (n) {
        if (n === '...') return

        const c = ['hover:bg-red-400']
        if (n === this.page) c.push('bg-red-500')
        else c.push('bg-gray-300')

        return c
      },

      // search
      close () {
        this.searchActive = false
        this.form.search = null
      },
      hide () {
        setTimeout(() => {
          this.searchActive = false
          this.selected = 0
        }, 100)
      },
      magnify () {
        if (!this.searchActive) {
          this.searchActive = true
        } else {
          this.search()
        }
      },
      search () {
        if (!this.form.search || this.form.search.length < 2) return
        this.searching = true
        this.doSearch()
      },
      setResults (data = null) {
        if (this.lastNonSearchRoute) {
          this.$router.replace(`/?s=${this.form.search}`).catch(() => {})
        } else {
          this.lastNonSearchRoute = this.$route.path
          this.$router.push(`/?s=${this.form.search}`).catch(() => {})
        }
        this.searchResults = data
        this.searching = false
      },
      doSearch: debounce(function () {
        once({ params: { s: this.form.search } }).then(({ data }) => {
          this.setResults(data)
        }).catch(() => {
          // 404
          this.setResults([])
        })
      }, 200),

      maybeSearch () {
        if (this.hasSearch) {
          this.magnify()
        }
      },

      zuruck () {
        const to = this.lastNonSearchRoute || '/'
        if (to !== this.$route.fullPath) this.$router.push(to)
      },
      onMouseMove () {
        clearTimeout(this.afterHover)
        this.hoverEnabled = true
        this.afterHover = setTimeout(() => {
          this.hoverEnabled = false
        }, 600)
      }
    },
    beforeRouteEnter (to, from, next) {
      const { page, category, tag } = to.params
      const { s } = to.query
      if (s) {
        next()
      } else {
        store.dispatch('getPosts', { page, category, tag }).then(() => {
          next((vm) => {
            if (!vm.hasSearch) {
              vm.searchResults = null
              vm.close()
            }
          })
        })
      }
    },
    beforeRouteUpdate (to, from, next) {
      const { page, category, tag } = to.params
      const { s } = to.query
      if (s) {
        this.form.search = s
        next()
      } else {
        this.searchResults = null
        this.close()
        store.dispatch('getPosts', { page, category, tag }).then(next)
      }
    },
    mounted () {
      if (this.hasSearch) {
        this.form.search = this.hasSearch
        if (this.searching) return
        this.doSearch()
      }
    },
    watch: {
      filters: {
        deep: true,
        handler ({ filter }) {
          let q = { ...this.filters }
          cleanObj(q)
          // handle multis
          if (filter && filter.includes('multi')) {
            let [f, x] = q.filter.split('-')
            q.filter = f
            q.x = x
          }
          const cf = this.$route.query.filter
          const cx = this.$route.query.x
          if (cf !== q.filter || cx !== q.x) {
            delete q.page
          }
          this.$router.push({ name: 'Feed', query: { ...q } })
        }
      }
    },
    directives: {
      focus: {
        inserted (el) {
          el.focus()
        }
      }
    },
    components: { PostRow, SearchRow, Close }
  }

  function cleanObj (o) {
    for (let n in o) {
      if ((o[n] === null || o[n] === undefined) ||
        (typeof o[n] === 'number' && o[n] === 1) ||
        (typeof o[n] === 'boolean' && !o[n])) {
        delete o[n]
      }
    }
  }
</script>
